import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const INVOICING_FEE_PRODUCT_ID = "invoicing_fee";
const DEFAULT_VAT_RATE = 21;

const colors = {
  ink: "#0f172a",
  muted: "#64748b",
  line: "#dbe4f0",
  panel: "#f8fafc",
  primary: "#1f6fff",
  secondary: "#e84bcb",
  success: "#0f9f6e",
};

const styles = StyleSheet.create({
  page: {
    padding: 34,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: colors.ink,
    backgroundColor: "#ffffff",
  },
  hero: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 20,
    padding: 20,
    borderRadius: 18,
    backgroundColor: colors.panel,
    border: `1 solid ${colors.line}`,
    marginBottom: 22,
  },
  brandTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: colors.ink,
    marginBottom: 6,
  },
  muted: {
    color: colors.muted,
    lineHeight: 1.45,
  },
  receiptBlock: {
    alignItems: "flex-end",
  },
  receiptTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.primary,
    marginBottom: 8,
    textTransform: "uppercase",
  },
  statusPaid: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 999,
    color: colors.success,
    backgroundColor: "#dcfce7",
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 10,
  },
  statusPending: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 999,
    color: "#b45309",
    backgroundColor: "#fef3c7",
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 10,
  },
  metaLine: {
    color: colors.muted,
    marginBottom: 3,
    textAlign: "right",
  },
  sectionRow: {
    flexDirection: "row",
    gap: 14,
    marginBottom: 18,
  },
  infoCard: {
    flex: 1,
    padding: 14,
    borderRadius: 14,
    border: `1 solid ${colors.line}`,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: colors.primary,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginBottom: 8,
  },
  lineText: {
    marginBottom: 4,
    lineHeight: 1.35,
  },
  table: {
    borderRadius: 14,
    border: `1 solid ${colors.line}`,
    overflow: "hidden",
    marginBottom: 16,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#0f172a",
    color: "#ffffff",
  },
  tableRow: {
    flexDirection: "row",
    borderTop: `1 solid ${colors.line}`,
  },
  tableRowAlt: {
    backgroundColor: "#fbfdff",
  },
  th: {
    padding: 8,
    fontSize: 9,
    fontWeight: 700,
  },
  td: {
    padding: 8,
    fontSize: 9,
    color: colors.ink,
  },
  descCol: { width: "34%" },
  qtyCol: { width: "10%", textAlign: "right" },
  priceCol: { width: "16%", textAlign: "right" },
  vatCol: { width: "12%", textAlign: "right" },
  exclCol: { width: "14%", textAlign: "right" },
  totalCol: { width: "14%", textAlign: "right" },
  totalsWrap: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 2,
    marginBottom: 20,
  },
  totalsBox: {
    width: 230,
    padding: 14,
    borderRadius: 14,
    backgroundColor: colors.panel,
    border: `1 solid ${colors.line}`,
  },
  totalLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 7,
  },
  totalFinal: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
    paddingTop: 10,
    borderTop: `1 solid ${colors.line}`,
  },
  totalFinalText: {
    fontSize: 13,
    fontWeight: 700,
    color: colors.primary,
  },
  paymentCard: {
    padding: 14,
    borderRadius: 14,
    border: `1 solid ${colors.line}`,
    marginBottom: 18,
  },
  footer: {
    marginTop: "auto",
    paddingTop: 12,
    borderTop: `1 solid ${colors.line}`,
    color: colors.muted,
    fontSize: 8,
    lineHeight: 1.4,
    textAlign: "center",
  },
});

const toNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeVatRate = (value) => {
  const parsed = toNumber(value, DEFAULT_VAT_RATE);
  return parsed > 0 ? parsed : DEFAULT_VAT_RATE;
};

const formatMoney = (value) =>
  `${new Intl.NumberFormat("fr-BE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(value))} €`;

const formatDateTime = (value) => {
  const timestamp = toNumber(value, null);
  if (!timestamp) return "-";
  return new Intl.DateTimeFormat("fr-BE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
};

const getUnitPrice = (product) => {
  if (Number.isFinite(Number(product?.unitPriceTvac))) {
    return Number(product.unitPriceTvac) / 100;
  }
  return toNumber(product?.price);
};

const normalizeRows = (invoice) => {
  const productRows = (invoice?.products || []).map((product) => {
    const quantity = Math.max(toNumber(product?.quantity, 1), 1);
    const unitPrice = getUnitPrice(product);
    const vatRate = normalizeVatRate(product?.vat ?? product?.vatRate ?? product?.vatOut);
    const totalIncl = unitPrice * quantity;
    const totalExcl = totalIncl / (1 + vatRate / 100);

    return {
      id: product?.id || product?.name || product?.title,
      name: product?.name || product?.title || "Produit",
      quantity,
      unitPrice,
      vatRate,
      totalIncl,
      totalExcl,
      vatAmount: totalIncl - totalExcl,
    };
  });

  const feeCents = toNumber(invoice?.invoicingFeeCents);
  if (feeCents > 0 && !productRows.some((row) => row.id === INVOICING_FEE_PRODUCT_ID)) {
    const totalIncl = feeCents / 100;
    const vatRate = DEFAULT_VAT_RATE;
    const totalExcl = totalIncl / (1 + vatRate / 100);
    productRows.push({
      id: INVOICING_FEE_PRODUCT_ID,
      name: "Frais de facturation",
      quantity: 1,
      unitPrice: totalIncl,
      vatRate,
      totalIncl,
      totalExcl,
      vatAmount: totalIncl - totalExcl,
    });
  }

  return productRows;
};

const getPaidAmount = (invoice, rows) => {
  if (Number.isFinite(Number(invoice?.amountPaid))) return Number(invoice.amountPaid) / 100;
  if (Number.isFinite(Number(invoice?.totalDueCents))) return Number(invoice.totalDueCents) / 100;
  if (Number.isFinite(Number(invoice?.totalAmountCents))) return Number(invoice.totalAmountCents) / 100;
  if (Number.isFinite(Number(invoice?.totalAmount))) return Number(invoice.totalAmount);
  return rows.reduce((sum, row) => sum + row.totalIncl, 0);
};

const ReceiptPDF = ({ invoice, organization }) => {
  const rows = normalizeRows(invoice);
  const subtotalExcl = rows.reduce((sum, row) => sum + row.totalExcl, 0);
  const vatTotal = rows.reduce((sum, row) => sum + row.vatAmount, 0);
  const rowsTotal = rows.reduce((sum, row) => sum + row.totalIncl, 0);
  const paidAmount = getPaidAmount(invoice, rows);
  const isPaid = Boolean(invoice?.paid || invoice?.checkoutPaymentStatus === "paid");
  const receiptNumber = invoice?.invoiceNumber || invoice?.id || "-";
  const customerEmail = invoice?.customerEmail || invoice?.stripeCustomerEmail || "-";
  const paymentDate = invoice?.paidAt || invoice?.updatedAt || invoice?.createdAt;

  return (
    <Document title={`Recu-${receiptNumber}`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.hero}>
          <View>
            <Text style={styles.brandTitle}>
              {organization?.legalName || organization?.organization || invoice?.orgName || "Comitty"}
            </Text>
            {organization?.companyNumber && <Text style={styles.muted}>N° entreprise : {organization.companyNumber}</Text>}
            {organization?.vatNumber && <Text style={styles.muted}>TVA : {organization.vatNumber}</Text>}
            {(organization?.businessAddress || organization?.address) && (
              <Text style={styles.muted}>{organization.businessAddress || organization.address}</Text>
            )}
            {organization?.email && <Text style={styles.muted}>{organization.email}</Text>}
            {organization?.phone && <Text style={styles.muted}>{organization.phone}</Text>}
          </View>

          <View style={styles.receiptBlock}>
            <Text style={styles.receiptTitle}>Reçu</Text>
            <Text style={isPaid ? styles.statusPaid : styles.statusPending}>
              {isPaid ? "Paiement reçu" : "Paiement en attente"}
            </Text>
            <Text style={styles.metaLine}>Reçu n° {receiptNumber}</Text>
            <Text style={styles.metaLine}>Commande : {invoice?.orderNumbers || invoice?.orderIds?.join(", ") || invoice?.id}</Text>
            <Text style={styles.metaLine}>Date : {formatDateTime(paymentDate)}</Text>
          </View>
        </View>

        <View style={styles.sectionRow}>
          <View style={styles.infoCard}>
            <Text style={styles.sectionTitle}>Client</Text>
            {invoice?.customerCompanyName && <Text style={styles.lineText}>{invoice.customerCompanyName}</Text>}
            <Text style={styles.lineText}>{customerEmail}</Text>
            {invoice?.customerInvoiceAddress && <Text style={styles.lineText}>{invoice.customerInvoiceAddress}</Text>}
            {invoice?.customerVatNumber && <Text style={styles.lineText}>TVA : {invoice.customerVatNumber}</Text>}
          </View>

          <View style={styles.infoCard}>
            <Text style={styles.sectionTitle}>Paiement</Text>
            <Text style={styles.lineText}>Méthode : {invoice?.paymentMethod || "Carte"}</Text>
            <Text style={styles.lineText}>Devise : {(invoice?.currency || "EUR").toUpperCase()}</Text>
            {invoice?.stripeSessionId && <Text style={styles.lineText}>Session : {invoice.stripeSessionId}</Text>}
            {invoice?.paymentIntentId && <Text style={styles.lineText}>Référence paiement : {invoice.paymentIntentId}</Text>}
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.th, styles.descCol]}>Description</Text>
            <Text style={[styles.th, styles.qtyCol]}>Qté</Text>
            <Text style={[styles.th, styles.priceCol]}>Prix un.</Text>
            <Text style={[styles.th, styles.vatCol]}>TVA</Text>
            <Text style={[styles.th, styles.exclCol]}>HTVA</Text>
            <Text style={[styles.th, styles.totalCol]}>TVAC</Text>
          </View>
          {rows.map((row, index) => (
            <View key={`${row.id}-${index}`} style={[styles.tableRow, index % 2 ? styles.tableRowAlt : null]}>
              <Text style={[styles.td, styles.descCol]}>{row.name}</Text>
              <Text style={[styles.td, styles.qtyCol]}>{row.quantity}</Text>
              <Text style={[styles.td, styles.priceCol]}>{formatMoney(row.unitPrice)}</Text>
              <Text style={[styles.td, styles.vatCol]}>{row.vatRate}%</Text>
              <Text style={[styles.td, styles.exclCol]}>{formatMoney(row.totalExcl)}</Text>
              <Text style={[styles.td, styles.totalCol]}>{formatMoney(row.totalIncl)}</Text>
            </View>
          ))}
        </View>

        <View style={styles.totalsWrap}>
          <View style={styles.totalsBox}>
            <View style={styles.totalLine}>
              <Text>Total HTVA</Text>
              <Text>{formatMoney(subtotalExcl)}</Text>
            </View>
            <View style={styles.totalLine}>
              <Text>Total TVA</Text>
              <Text>{formatMoney(vatTotal)}</Text>
            </View>
            <View style={styles.totalLine}>
              <Text>Total TVAC</Text>
              <Text>{formatMoney(rowsTotal)}</Text>
            </View>
            <View style={styles.totalFinal}>
              <Text style={styles.totalFinalText}>Montant payé</Text>
              <Text style={styles.totalFinalText}>{formatMoney(paidAmount)}</Text>
            </View>
          </View>
        </View>

        <View style={styles.paymentCard}>
          <Text style={styles.sectionTitle}>Informations du reçu</Text>
          <Text style={styles.lineText}>
            Ce document confirme le paiement de la commande référencée ci-dessus.
          </Text>
          <Text style={styles.lineText}>
            Les montants sont exprimés en EUR et incluent les taxes applicables.
          </Text>
        </View>

        <Text style={styles.footer}>
          Reçu généré par Comitty pour le compte de {organization?.legalName || organization?.organization || invoice?.orgName || "l'organisation"}.
        </Text>
      </Page>
    </Document>
  );
};

export default ReceiptPDF;
